import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import moment from 'moment/moment';

import { formatPrice } from 'common/formatters';
import {
  CONCESSION_SCHEDULE_TYPE_FIRST_MONTH,
  CONCESSION_SCHEDULE_TYPE_SECOND_MONTH,
  CONCESSION_SCHEDULE_TYPE_THIRD_MONTH,
} from 'common/concessionConstants';

import { hasRoleAnonymous } from 'common/userRoles';

import { createFormValidationFn, validateFormFieldsAndSubmit } from 'client/helpers/formikHelpers';

import SrCard from 'client/components/SrCard/SrCard';
import SrText from 'client/components/SrText';
import SrFlexRow from 'client/components/SrFlexRow/SrFlexRow';
import SrConcessionsBanner from 'client/components/SrConcessionsBanner/SrConcessionsBanner';
import SrBox from 'client/components/SrBox';
import SrTextList from 'client/components/SrTextList';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_RED_INVERTED,
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_GREEN,
} from 'client/components/SrButton/SrButton';
import SrFormikField, { SR_FIELD_THEME_PARTNER_FORM_ADOBE } from 'client/components/SrFormikField';
import SrConcessionsHistoryDataTable from 'client/components/SrConcessionsHistoryDataTable/SrConcessionsHistoryDataTable';

import useConcessionsProController from 'client/components/SrConcessionsPro/useConcessionsProController';

import './SrConcessionsPro.less';

const getFormFields = ({ rentPrice }) => {
  return [
    {
      id: 'amount',
      type: 'currency',
      title: 'Concession Amount',
      properties: {
        placeholder: 'Write the concession amount in dollars.',
        upperLimit: rentPrice,
        lowerLimit: 1,
        prefix: '$',
      },
      validations: ['required', 'betweenNumbers'],
    },
    {
      id: 'endDate',
      title: 'Concession End Date',
      titleSecondary: '(Renters must move in by this date to receive the concession)',
      type: 'date_picker',
      properties: {
        placeholder: 'MM/DD/YYYY',
        dateFormat: 'MM/DD/YYYY',
        isValidDate: (current) => current.isAfter(moment().startOf('day')),
      },
      validations: ['required', 'noPastDates'],
    },
    {
      id: 'scheduleType',
      title: 'Month Concession Applies',
      type: 'select',
      properties: {
        placeholder: 'Choose Month',
        enableResetOption: true,
        choices: [
          {
            value: CONCESSION_SCHEDULE_TYPE_FIRST_MONTH,
            label: 'First Month',
          },
          {
            value: CONCESSION_SCHEDULE_TYPE_SECOND_MONTH,
            label: 'Second Month',
          },
          {
            value: CONCESSION_SCHEDULE_TYPE_THIRD_MONTH,
            label: 'Third Month',
          },
        ],
      },
      validations: ['required'],
    },
  ];
};

const SrConcessionsPro = ({ concession, rentPrice, listingAddress, listingId, currentUser }) => {
  const [endConcessionModalOpen, setEndConcessionModalOpen] = useState(false);
  const controller = useConcessionsProController({ concession });
  const isAnonymous = hasRoleAnonymous(currentUser.roles);

  if (controller.isCreatingConcession) {
    return (
      <SrCard maxWidth="100%" padding="20px 16px" styleName="SrConcessionsPro">
        <SrBox>
          <SrFlexRow styleName="headerContainer">
            <SrBox styleName="starIconContainer">
              <SrBox styleName="starIcon" />
            </SrBox>
            <SrText
              font="greycliff"
              fontSize="18px"
              weight="700"
              lineHeight="38px"
              letterSpacing="1px"
            >
              Concession Pro&trade;
            </SrText>
          </SrFlexRow>
          <SrFlexRow align="baseline" width="100%">
            <SrText font="greycliff" weight="700" fontSize="18px" color="green">
              Attract more leads&nbsp;
              <SrText font="greycliff" weight="700" fontSize="18px" color="grey">
                and increase occupancy with tailored rent concessions.
              </SrText>
            </SrText>
          </SrFlexRow>
        </SrBox>
        <SrBox margin="12px 0px">
          <SrBox>
            <SrFlexRow>
              <SrText font="greycliff" weight="700" fontSize="16px" color="blueGreyCliff">
                {listingAddress}
              </SrText>
            </SrFlexRow>
            <SrFlexRow>
              <SrText font="greycliff" fontSize="17px" color="blueGreyCliff">
                Current Rent Rate:&nbsp;
                <SrText font="greycliff" fontSize="16px" color="blueGreyCliff" weight="700">
                  {formatPrice(rentPrice)}
                </SrText>
              </SrText>
            </SrFlexRow>
          </SrBox>
        </SrBox>
        <Formik
          initialValues={{}}
          validate={(formValues) => {
            const formFields = getFormFields({ formValues, rentPrice });
            return createFormValidationFn(formFields)(formValues);
          }}
          onSubmit={(formValues) =>
            controller.onCreateConcessionSubmit({
              listingId,
              amount: formValues.amount,
              endDate: moment(formValues.endDate).format('YYYY-MM-DD'),
              scheduleType: formValues.scheduleType,
            })
          }
        >
          {(formikBag) => {
            const formFields = getFormFields({ rentPrice });
            return (
              <>
                <Form className="form">
                  {formFields.map((formField) => (
                    <SrBox key={formField.id} marginBottom="16px">
                      <SrFormikField
                        formField={formField}
                        formikBag={formikBag}
                        theme={SR_FIELD_THEME_PARTNER_FORM_ADOBE}
                        margin={formField.margin}
                      />
                    </SrBox>
                  ))}
                </Form>
                <SrBox marginTop="8px">
                  <SrButton
                    onClick={controller.onCancelConcessionCreation}
                    theme={SR_BUTTON_THEME_ADOBE_GREY}
                  >
                    Cancel
                  </SrButton>
                </SrBox>
                <SrBox marginTop="8px">
                  <SrButton
                    onClick={() =>
                      validateFormFieldsAndSubmit({
                        formikBag,
                        formFields,
                      })
                    }
                    theme={SR_BUTTON_THEME_ADOBE_GREEN}
                    disabled={isAnonymous}
                  >
                    Create Concession
                  </SrButton>
                </SrBox>
              </>
            );
          }}
        </Formik>
      </SrCard>
    );
  }

  if (controller.hasActiveConcession) {
    return (
      <SrCard noContentPadding maxWidth="100%" styleName="SrConcessionsPro">
        <SrBox padding="0 16px">
          <SrFlexRow styleName="headerContainer">
            <SrBox styleName="starIconContainer">
              <SrBox styleName="starIcon" />
            </SrBox>
            <SrText
              font="greycliff"
              fontSize="18px"
              weight="700"
              lineHeight="38px"
              letterSpacing="1px"
            >
              Concession Pro&trade;
            </SrText>
          </SrFlexRow>
          <SrFlexRow align="baseline" width="100%">
            <SrText font="greycliff" weight="700" fontSize="18px" color={controller.color}>
              {controller.text}
            </SrText>
            <SrText
              font="greycliff"
              color="blueGreyCliff"
              weight="600"
              fontSize="16px"
              paddingLeft="16px"
              lineHeight="25px"
            >
              {controller.subtext}
            </SrText>
          </SrFlexRow>
        </SrBox>
        <SrBox margin="18px 16px">
          <SrConcessionsBanner concession={concession} rentPrice={rentPrice} />
          <SrBox marginTop="16px" />
          {controller.endConcessionCtaActive && (
            <SrButton
              onClick={() => setEndConcessionModalOpen(true)}
              theme={SR_BUTTON_THEME_ADOBE_RED_INVERTED}
              disabled={isAnonymous}
            >
              End concession
            </SrButton>
          )}
          {controller.startConcessionCtaActive && (
            <SrButton
              onClick={controller.onStartConcessionCreation}
              theme={SR_BUTTON_THEME_ADOBE_GREEN}
              disabled={isAnonymous}
            >
              Create Concession
            </SrButton>
          )}
        </SrBox>
        {endConcessionModalOpen && (
          <SrSimpleMessageModal
            iconUrl="/images/warning-triangle-orange-large.svg"
            maxWidth={375}
            headline="End special early?"
            theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
            message={
              <span>
                You are about to end the rent concession earlier than the stated duration.
                <br />
                <br />
                {'Be aware that this action may result in confusion or disappointment for interested ' +
                  'parties who were expecting the concession to be available for the full duration.'}
              </span>
            }
            customCtas={[
              <SrBox key="endSpecial">
                <SrButton
                  onClick={() => {
                    controller.onEndConcessionSubmit().then(() => setEndConcessionModalOpen(false));
                  }}
                  theme={SR_BUTTON_THEME_ADOBE_RED_INVERTED}
                >
                  End special
                </SrButton>
              </SrBox>,
              <SrBox key="cancel" marginTop="16px">
                <SrButton
                  onClick={() => setEndConcessionModalOpen(false)}
                  theme={SR_BUTTON_THEME_ADOBE_GREY}
                >
                  Cancel
                </SrButton>
              </SrBox>,
            ]}
          />
        )}
        <SrBox width="100%" marginTop="24px">
          <SrBox margin="12px 16px">
            <SrText font="greycliff" weight="700" fontSize="16px">
              Concession History
            </SrText>
          </SrBox>
          <SrConcessionsHistoryDataTable listingId={listingId} />
        </SrBox>
      </SrCard>
    );
  }

  return (
    <SrCard maxWidth="100%" padding="20px 16px" styleName="SrConcessionsPro">
      <SrBox>
        <SrFlexRow styleName="headerContainer">
          <SrBox styleName="starIconContainer">
            <SrBox styleName="starIcon" />
          </SrBox>
          <SrText
            font="greycliff"
            fontSize="18px"
            weight="700"
            lineHeight="38px"
            letterSpacing="1px"
          >
            Concession Pro&trade;
          </SrText>
        </SrFlexRow>
        <SrFlexRow align="baseline" width="100%">
          <SrText font="greycliff" weight="700" fontSize="18px" color="green">
            Attract more leads&nbsp;
            <SrText font="greycliff" weight="700" fontSize="18px" color="grey">
              and increase occupancy with tailored rent concessions.
            </SrText>
          </SrText>
        </SrFlexRow>
      </SrBox>
      <SrBox margin="12px 0px 32px 0px">
        <SrText font="greycliff" weight="700" fontSize="16px">
          What You Can Do
        </SrText>
        <SrTextList
          font="greycliff"
          weight="700"
          fontSize="16px"
          lineHeight="24px"
          textList={[
            'Reach thousands of renters on Sunroom, Zillow and more',
            'Blast all leads with this concession with automated text, email, and push notifications.',
            'Highlight the concession with a unique watermark added to photos',
            'Track lead and application activity from your campaign',
          ]}
        />
      </SrBox>
      <SrButton
        onClick={controller.onStartConcessionCreation}
        theme={SR_BUTTON_THEME_ADOBE_GREEN}
        disabled={isAnonymous}
      >
        Create Concession
      </SrButton>
      {concession && (
        <SrBox width="100%" marginTop="24px">
          <SrBox marginBottom="12px">
            <SrText font="greycliff" weight="700" fontSize="16px">
              Concession History
            </SrText>
          </SrBox>
          <SrConcessionsHistoryDataTable listingId={listingId} />
        </SrBox>
      )}
    </SrCard>
  );
};

SrConcessionsPro.propTypes = {
  concession: PropTypes.object,
  listingId: PropTypes.number.isRequired,
  listingAddress: PropTypes.string.isRequired,
  rentPrice: PropTypes.number.isRequired,
  currentUser: PropTypes.object.isRequired,
};

SrConcessionsPro.displayName = 'SrConcessionsPro';

export default SrConcessionsPro;
